// Resolves "what is actually deciding this merchant's courier right now?"
//
// ─────────────────────────────────────────────────────────────────────────────
//  READ THIS BEFORE EDITING
//  This file duplicates precedence logic that properly belongs to the Java
//  backend. It exists because a merchant's allocation can be changed from seven
//  different screens writing five different fields on four different APIs, and
//  nothing anywhere told an ops user which one was in effect -- so people edited
//  rules that had been inert for months.
//
//  The correct fix is a server-resolved `effectiveAllocationStrategy` on
//  GET /config/merchant/{id} (see the Merchant Setup audit, Part 6). When that
//  lands, DELETE this file and read the field instead. Until then, any change to
//  allocation precedence in proship_mongo must be mirrored here.
//
//  Backend source of truth:
//    MerchantConfig.allocationEngineVersion   "V2" => PACE ranks (live)
//    MerchantConfig.paceV2ShadowEnabled       PACE scored but NOT used (shadow)
//    MerchantConfig.automatedCourierAllocationEnabled  allows the PACE entry in rule uploads;
//                                             without PACE live those rules book in their own order
//                                             (the old rule/Lambda ranker was removed in Nov 2026)
//    MerchantConfig.cheapestCPList / MongoMerchant.mappedOrderRule  legacy rules
//    MongoMerchant.allocationVersion          published D2C allocation wizard
// ─────────────────────────────────────────────────────────────────────────────

export const STRATEGY = {
    PACE:      'PACE',
    RULES:     'RULES',
    WIZARD:    'WIZARD',
    LEGACY:    'LEGACY',
    UNCONFIGURED: 'UNCONFIGURED',
};

/**
 * @param cfg  the merchant_config document (GET /config/merchant/{id})
 * @param mer  the merchant document (GET /merchant/{id})
 * @returns { strategy, label, detail, inert, tone }
 *          `inert` lists the things that are configured but NOT deciding anything.
 */
export function resolveAllocationStrategy(cfg = {}, mer = {}) {
    const engine = (cfg.allocationEngineVersion || '').toUpperCase();
    const shadow = !!cfg.paceV2ShadowEnabled;
    const paceInRules = !!cfg.automatedCourierAllocationEnabled;
    const hasLegacyRules = !!(cfg.cheapestCPList?.length || mer.mappedOrderRule?.rules?.length);
    const wizardVersion = mer.allocationVersion;

    const inert = [];
    const push = (what, why) => inert.push({ what, why });

    if (engine === 'V2') {
        if (hasLegacyRules) push('Legacy cheapest / custom courier rules', 'PACE ignores them');
        return {
            strategy: STRATEGY.PACE,
            tone: 'ok',
            label: 'PACE is deciding allocations',
            detail: 'PACE ranks the couriers of every allocation rule whose priority 1 is the PACE entry. Rules without it book in their own order.',
            inert,
        };
    }

    if (paceInRules) {
        if (hasLegacyRules) push('Legacy cheapest / custom courier rules', 'the allocation rules take precedence');
        return {
            strategy: STRATEGY.RULES,
            tone: 'info',
            label: 'Allocation rules are deciding allocations',
            detail: shadow
                ? 'PACE is not live, so every rule books in its own courier order. PACE also scores every order in shadow — recorded for comparison, never used.'
                : 'PACE is not live, so every rule books in its own courier order.',
            inert,
        };
    }

    if (wizardVersion) {
        if (hasLegacyRules) push('Legacy cheapest / custom courier rules', 'the published allocation set takes precedence');
        return {
            strategy: STRATEGY.WIZARD,
            tone: 'info',
            label: `Published allocation set ${wizardVersion} is in effect`,
            detail: mer.allocationEffFrom
                ? `Published by ${mer.allocationPublishedBy || 'an internal user'}, effective from ${mer.allocationEffFrom}.`
                : 'Set from Onboarding → Step 3 · Allocation.',
            inert,
        };
    }

    if (hasLegacyRules) {
        return {
            strategy: STRATEGY.LEGACY,
            tone: 'warn',
            label: 'Legacy courier rules are deciding allocations',
            detail: 'No allocation engine is enabled, so the older cheapest/custom rule list is what routes orders.',
            inert,
        };
    }

    return {
        strategy: STRATEGY.UNCONFIGURED,
        tone: 'warn',
        label: 'No allocation is configured',
        detail: 'Orders fall back to the platform default. Set this up from Onboarding → Step 3 · Allocation.',
        inert,
    };
}
